import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Flex, Modal, Space, Spin, theme } from 'antd';
import Text from 'admin/components/common/Text';
import DesignModeControl from 'admin/components/common/DesignModeControl';
import { primaryFontFamily, canonicalFamilyName } from 'admin/components/common/AI/Chat/fonts/font-resolver';
import type { DesignDefinition, DesignModeSelection, DesignPreset } from 'common/design-tokens';
import { getPlatformReferenceWidth } from 'common/utils/story-media-query';
import type { SCFontItem } from 'types/fonts';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import t from 'utils/translate';
import DesignPresetPlatformLabel from './DesignPresetPlatformLabel';
import { getDesignPresetPreviewStyle } from './design-preset-display';
import css from './DesignPresetsEditor.scss';

export type DesignPresetPreviewFont = Pick<SCFontItem, 'fontFamily'> &
	Partial<Pick<SCFontItem, 'url' | 'fontType' | 'weight' | 'style'>>;

type PreviewPlatform = { key: 'base' | StoryDefaultPlatform; label: string };

/** Mounted only while preview is open; font registrations belong to this dialog. */
export default function DesignPresetPreview({
	preset,
	design,
	mediaQuery,
	platforms,
	fonts,
	onClose,
}: {
	preset: DesignPreset;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	platforms: PreviewPlatform[];
	fonts: DesignPresetPreviewFont[];
	onClose: () => void;
}) {
	const { token } = theme.useToken();
	const previewModeStyles = {
		color: token.colorText,
		'--token-editor-text': token.colorText,
		'--token-editor-muted': token.colorTextSecondary,
		'--token-editor-border': token.colorBorder,
		'--resource-surface': token.colorFillSecondary,
	};
	const [requestedModes, setRequestedModes] = useState<DesignModeSelection>({});
	const modeSelection = useMemo(
		() =>
			Object.fromEntries(
				design.modeGroups.map(group => [
					group.id,
					group.modes.some(mode => mode.id === requestedModes[group.id])
						? requestedModes[group.id]
						: group.defaultModeId,
				])
			),
		[design.modeGroups, requestedModes]
	);
	const colorScheme =
		design.modeGroups
			.flatMap(group => group.modes.filter(mode => mode.id === modeSelection[group.id]))
			.find(mode => mode.colorScheme)?.colorScheme ?? 'light';
	const samples = useMemo(
		() =>
			platforms.map(platform => ({
				...platform,
				referenceWidth:
					platform.key === 'base' ? undefined : getPlatformReferenceWidth(mediaQuery, platform.key),
				style: getDesignPresetPreviewStyle({
					preset,
					platform: platform.key,
					mediaQuery,
					design,
					modeSelection,
				}),
			})),
		[platforms, preset, mediaQuery, design, modeSelection]
	);
	// Color-only mode changes keep loaded faces; typography changes reload the required fonts.
	const fontRequests = JSON.stringify(
		samples.flatMap(sample =>
			sample.style.fontFamily
				? [
						{
							family: primaryFontFamily(sample.style.fontFamily) ?? 'sans-serif',
							weight: sample.style.fontWeight ?? 400,
						},
					]
				: []
		)
	);
	const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(fontRequests === '[]' ? 'ready' : 'loading');
	useEffect(() => {
		if (fontRequests === '[]') {
			setStatus('ready');
			return undefined;
		}
		let active = true;
		const requiredFonts: { family: string; weight: string | number }[] = JSON.parse(fontRequests);
		const faces: FontFace[] = [];
		const links: HTMLLinkElement[] = [];
		setStatus('loading');
		const families = new Set(requiredFonts.map(font => canonicalFamilyName(font.family)));
		const selected = fonts.filter(
			font => font.url && families.has(canonicalFamilyName(primaryFontFamily(font.fontFamily) ?? ''))
		);
		const load = async () => {
			await Promise.all(
				selected.map(async font => {
					if (font.fontType === 'google') {
						await new Promise<void>((resolve, reject) => {
							const link = document.createElement('link');
							link.rel = 'stylesheet';
							// Stored Google entries use either legacy variants or CSS2 axis syntax.
							link.href = `https://fonts.googleapis.com/${font.url!.includes('@') ? 'css2' : 'css'}?family=${font.url}&display=swap`;
							link.onload = () => resolve();
							link.onerror = () => reject(new Error('Font stylesheet failed to load.'));
							links.push(link);
							document.head.appendChild(link);
						});
					} else {
						const face = new FontFace(
							primaryFontFamily(font.fontFamily)!,
							`url(${JSON.stringify(font.url)})`,
							{
								weight: String(font.weight || 400),
								style: font.style?.replace(/['"]+/g, '').toLowerCase() || 'normal',
							}
						);
						await face.load();
						if (active) {
							document.fonts.add(face);
							faces.push(face);
						}
					}
				})
			);
			if (!active) return;
			await Promise.all(
				requiredFonts.map(async ({ family, weight }) => {
					const loaded = await document.fonts.load(
						`${weight} 16px ${JSON.stringify(family)}`,
						t('presets.sample')
					);
					const local =
						/^(serif|sans-serif|monospace|system-ui|cursive|fantasy|Arial|Helvetica|Verdana|Georgia|Times New Roman|Courier New)$/i.test(
							family
						);
					if (!loaded.length && !local) throw new Error('Preview font is unavailable.');
				})
			);
			if (active) setStatus('ready');
		};
		load().catch(() => {
			if (active) setStatus('error');
		});
		return () => {
			active = false;
			links.forEach(link => link.remove());
			faces.forEach(face => document.fonts.delete(face));
		};
	}, [fonts, fontRequests]);
	return (
		<Modal
			open
			title={
				<span className={css.previewTitle}>
					<span>{preset.name}</span>
					<span className={css.previewCaption}>{t('presets.preview')}</span>
				</span>
			}
			onCancel={onClose}
			footer={null}
			width={960}
		>
			<Flex gap="small" align="center" wrap className={css.previewModes} style={previewModeStyles}>
				{design.modeGroups.map(group => (
					<Space key={group.id}>
						<Text tag="span" compact size={Text.size.body}>
							{group.name}
						</Text>
						<DesignModeControl
							compact
							ariaLabel={`${group.name} preview mode`}
							value={modeSelection[group.id]}
							modes={group.modes}
							onChange={id => setRequestedModes(current => ({ ...current, [group.id]: id }))}
						/>
					</Space>
				))}
			</Flex>
			{status === 'loading' ? (
				<div className={css.previewLoading}>
					<Spin />
					<span>{t('presets.loadingFonts')}</span>
				</div>
			) : null}
			{status === 'error' ? <Alert type="error" showIcon title={t('presets.previewFontError')} /> : null}
			{status === 'ready' ? (
				<div
					className={css.previewGrid}
					data-theme={colorScheme}
					role="region"
					aria-label={t('presets.preview')}
					// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard scrolling
					tabIndex={0}
				>
					{samples.map(sample => (
						<section className={css.previewPlatform} key={sample.key}>
							<h3 className={css.previewHeading}>
								<DesignPresetPlatformLabel
									platform={sample.key}
									defaultPlatform={mediaQuery.defaultPlatform}
									label={sample.label}
									referenceWidth={sample.referenceWidth}
								/>
							</h3>
							<div className={css.preview}>
								<div style={sample.style}>{t('presets.sample')}</div>
							</div>
						</section>
					))}
				</div>
			) : null}
		</Modal>
	);
}
